// Onboarding flow types

export interface OnboardingState {
  // Step 1: Pain point
  painPoint: PainPoint | null;
  
  // Step 2: Role definition
  role: RoleDefinition | null;
  
  // Step 3: Bar definition
  bar: BarDefinition | null;
  
  // Step 4: Candidate
  candidate: CandidateEvidence | null;
  
  // Step 5: AI Signal (computed)
  signal: AISignal | null;
  
  // Step 6: Decision
  decision: HiringDecision | null;
}

export type PainPoint =
  | 'distinguish_candidates'
  | 'interviewer_disagreement'
  | 'too_many_interviews'
  | 'resume_bias'
  | 'manager_time'
  | 'just_trying';

export const PAIN_POINTS: { id: PainPoint; label: string; description: string }[] = [
  {
    id: 'distinguish_candidates',
    label: 'Hard to distinguish candidates',
    description: 'We talk to many candidates but struggle to tell them apart',
  },
  {
    id: 'interviewer_disagreement',
    label: 'Interviewers disagree',
    description: 'Interviewers apply different bars or reach different conclusions',
  },
  {
    id: 'too_many_interviews',
    label: 'Too many interviews',
    description: 'We keep adding interviews just to feel confident',
  },
  {
    id: 'resume_bias',
    label: 'Credential bias',
    description: 'We rely too much on resumes, schools, or referrals',
  },
  {
    id: 'manager_time',
    label: 'Manager time drain',
    description: 'Hiring pulls managers away from product work',
  },
  {
    id: 'just_trying',
    label: 'Just exploring',
    description: "I just want to try it",
  },
];

export type LevelRange = 'junior' | 'mid' | 'senior' | 'staff';

export const LEVEL_RANGES: { id: LevelRange; label: string }[] = [
  { id: 'junior', label: 'Junior' },
  { id: 'mid', label: 'Mid' },
  { id: 'senior', label: 'Senior' },
  { id: 'staff', label: 'Staff+' },
];

export interface RoleDefinition {
  title: string;
  level: LevelRange;
  locations: string[];
  numberOfHires: number;
}

export type EmphasisLevel = 'low' | 'medium' | 'high';

export interface Principle {
  id: string;
  name: string;
  emphasis: EmphasisLevel;
  enabled: boolean;
}

export interface Competency {
  id: string;
  name: string;
  emphasis: EmphasisLevel;
  enabled: boolean;
}

export interface BarDefinition {
  principles: Principle[];
  competencies: Competency[];
}

export const DEFAULT_PRINCIPLES: Omit<Principle, 'id'>[] = [
  { name: 'Ownership', emphasis: 'medium', enabled: true },
  { name: 'Judgment', emphasis: 'medium', enabled: true },
  { name: 'Communication', emphasis: 'medium', enabled: true },
  { name: 'Collaboration', emphasis: 'medium', enabled: true },
];

export const ROLE_COMPETENCY_SUGGESTIONS: Record<string, string[]> = {
  engineer: ['Coding', 'System Design', 'Problem Solving', 'Code Review', 'Technical Communication'],
  designer: ['Visual Design', 'UX Research', 'Prototyping', 'Design Systems', 'Stakeholder Management'],
  product: ['Strategy', 'Prioritization', 'User Research', 'Data Analysis', 'Cross-functional Leadership'],
  data: ['SQL & Analytics', 'Statistical Modeling', 'Data Visualization', 'Experimentation', 'Stakeholder Communication'],
  default: ['Problem Solving', 'Execution', 'Communication', 'Domain Knowledge'],
};

export interface CandidateEvidence {
  name?: string;
  resumeFile?: File;
  linkedinContent?: string;
  portfolioUrl?: string;
  usingSample: boolean;
}

export type FitLevel = 'strong' | 'mixed' | 'weak';
export type ConfidenceLevel = 'low' | 'medium' | 'high';

export interface AISignal {
  roleFit: FitLevel;
  levelAlignment: string;
  strengths: string[];
  risks: string[];
  confidence: ConfidenceLevel;
  reasoning: string[];
  extractedSkills: string[];
}

export type DecisionLean = 'lean_yes' | 'lean_no' | 'need_signal';

export interface MissingSignal {
  area: string;
  suggestion: string;
}

export interface HiringDecision {
  lean: DecisionLean;
  missingSignals?: MissingSignal[];
}

export interface OnboardingStepProps {
  state: OnboardingState;
  onUpdate: (updates: Partial<OnboardingState>) => void;
  onNext: () => void;
  onBack: () => void;
}

